/**
 * Firewall Rules — per-domain custom firewall rules for the Security tab. Pick a
 * domain, then review its rules (ordered by priority) and toggle each one on/off
 * or delete it.
 *
 * Backed by `/waf/firewall/*` (Owl/Mlytics, customer-scoped). The schema-driven
 * rule builder (create/edit) lands separately; this surfaces the list + the
 * enable/disable + delete controls.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText, RotateCcw, Trash2, Pencil, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Badge,
  Skeleton,
  Button,
  IconButton,
  Switch,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafFirewallRules,
  useSetFirewallRuleEnabled,
  useDeleteFirewallRule,
  fmtEpoch,
  wafExtractError,
  type FirewallRule,
  type FirewallRuleAction,
  type FirewallRuleCondition,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import styles from './WafFirewallRules.module.css';

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;
const mono = { fontFamily: 'var(--vn-font-mono)' } as const;

/** Build the create/edit form URL (+ optional rule id). */
function formHref(serviceId: string, domain: string, id?: string) {
  const q = new URLSearchParams({ domain });
  if (id) q.set('id', id);
  return `/services/waf/${serviceId}/firewall/${id ? 'edit' : 'new'}?${q.toString()}`;
}

/** Action `type` → display label + badge tone. */
const ACTION_LABEL: Record<string, string> = {
  block: 'Block',
  pass: 'Allow',
  rate_limit: 'Rate limit',
  block_redirect: 'Redirect',
  challenge: 'Challenge',
};
const ACTION_TONE: Record<string, 'danger' | 'success' | 'warning' | 'neutral'> = {
  block: 'danger',
  pass: 'success',
  rate_limit: 'warning',
  block_redirect: 'neutral',
  challenge: 'neutral',
};

/** A short, human suffix for the action (rate / redirect / challenge specifics). */
function actionDetail(a: FirewallRuleAction): string {
  switch (a.type) {
    case 'rate_limit':
      return a.total_count ? `${a.total_count}/min` : '';
    case 'block_redirect':
      return a.code ? `${a.code}` : '';
    case 'challenge':
      return a.mode ?? '';
    default:
      return '';
  }
}

/** Compact one-line rendering of a condition. */
function condText(c: FirewallRuleCondition): string {
  const val = c.values?.length ? c.values.join(', ') : (c.value ?? '');
  const field = c.key ? `${c.field}[${c.key}]` : c.field;
  return [field, c.operator, val].filter(Boolean).join(' ');
}

/**
 * First couple conditions as text + a `+N` overflow. Very long condition values
 * (e.g. a big user-agent match list) are width-capped and clamped to a few lines
 * with an ellipsis so they don't blow out the table; full text is in the title.
 */
function conditionsCell(conds: FirewallRuleCondition[] | undefined, max = 2) {
  const items = conds ?? [];
  if (items.length === 0) return muted('—');
  const shown = items.slice(0, max).map(condText).join('  •  ');
  const overflow = items.length > max ? `  +${items.length - max}` : '';
  return (
    <span
      title={items.map(condText).join('\n')}
      style={{
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        maxWidth: 460,
        wordBreak: 'break-word',
        ...mono,
        fontSize: 'var(--vn-font-size-sm)',
      }}
    >
      {shown}
      {overflow ? <span style={{ color: 'var(--vn-fg-muted)' }}>{overflow}</span> : null}
    </span>
  );
}

/** Inline enable/disable toggle for a rule. */
function EnableCell({ serviceId, domain, rule }: { serviceId: string; domain: string; rule: FirewallRule }) {
  const { show } = useToast();
  const setEnabled = useSetFirewallRuleEnabled(serviceId, {
    onSuccess: (_d, vars) => { show({ tone: 'success', title: vars.enable ? 'Rule enabled' : 'Rule disabled' }); trackFeatureAction('waf', 'firewall_rule', vars.enable ? 'enable' : 'disable'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Switch
        checked={rule.enable}
        disabled={setEnabled.isPending}
        aria-label={`Enable ${rule.name}`}
        onCheckedChange={(enable) => setEnabled.mutate({ domain, id: rule.id, enable })}
      />
    </div>
  );
}

/** Edit + delete actions for a rule. */
function ActionsCell({ serviceId, domain, rule }: { serviceId: string; domain: string; rule: FirewallRule }) {
  const { show } = useToast();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteFirewallRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule deleted' }); trackFeatureAction('waf', 'firewall_rule', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        <Tooltip content="Edit" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Edit rule" onClick={() => navigate(formHref(serviceId, domain, rule.id))}>
            <Pencil size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Delete" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Delete rule" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} />
          </IconButton>
        </Tooltip>
      </TooltipProvider>
      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete rule?"
          description={`Delete the rule "${rule.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ domain, id: rule.id })}
        />
      )}
    </div>
  );
}

/** Firewall rules table for a domain. */
function RulesCard({ serviceId, domain }: { serviceId: string; domain: string }) {
  const navigate = useNavigate();
  const goAdd = () => navigate(formHref(serviceId, domain));
  const { data, isLoading, isError, refetch } = useWafFirewallRules(serviceId, domain);
  const rules = data ?? [];

  const columns = useMemo<ColumnDef<FirewallRule, unknown>[]>(
    () => [
      {
        id: 'priority',
        header: '#',
        enableSorting: false,
        cell: ({ row }) => muted(row.original.priority ?? '—'),
      },
      {
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name}</span>,
      },
      {
        id: 'conditions',
        header: 'Conditions',
        enableSorting: false,
        cell: ({ row }) => conditionsCell(row.original.conditions),
      },
      {
        id: 'action',
        header: 'Action',
        enableSorting: false,
        cell: ({ row }) => {
          const a = row.original.action;
          const detail = actionDetail(a);
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Badge tone={ACTION_TONE[a.type] ?? 'neutral'} appearance="subtle" size="sm">
                {ACTION_LABEL[a.type] ?? a.type}
              </Badge>
              {detail ? muted(detail) : null}
            </span>
          );
        },
      },
      {
        id: 'enable',
        header: 'Enabled',
        enableSorting: false,
        cell: ({ row }) => <EnableCell serviceId={serviceId} domain={domain} rule={row.original} />,
      },
      {
        id: 'updated',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }) => muted(fmtEpoch(row.original.updated_at)),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => <ActionsCell serviceId={serviceId} domain={domain} rule={row.original} />,
      },
    ],
    [serviceId, domain],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Firewall rules{rules.length ? ` (${rules.length})` : ''}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.toolbar}>
          <div className={styles.spacer} />
          <div className={styles.actions}>
            <Button variant="secondary" startIcon={<RotateCcw size={15} />} onClick={() => refetch()} aria-label="Refresh rules">
              Refresh
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={goAdd}>Add rule</Button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
          </div>
        ) : isError ? (
          <Alert tone="danger" title="Couldn't load firewall rules" actions={<button onClick={() => refetch()}>Retry</button>}>
            The WAF service couldn't return this domain's firewall rules. Try again.
          </Alert>
        ) : rules.length === 0 ? (
          <EmptyState
            icon={<ScrollText size={20} />}
            title="No firewall rules yet"
            description="This domain has no custom firewall rules. Create one to start filtering traffic."
            actions={<Button startIcon={<Plus size={15} />} onClick={goAdd}>Add rule</Button>}
          />
        ) : (
          <DataTable columns={columns} data={rules} getRowId={(r) => r.id} emptyState="No firewall rules." />
        )}
      </CardBody>
    </Card>
  );
}

export function WafFirewallRules({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<ScrollText size={22} />}
        title="Firewall Rules"
        description="Review and enable/disable the selected subdomain's custom firewall rules."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<ScrollText size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to manage firewall rules for. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<ScrollText size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage its firewall rules."
        />
      ) : (
        <RulesCard serviceId={serviceId} domain={domain} />
      )}
    </Container>
  );
}

export default WafFirewallRules;
